import { useEffect, useMemo, useState } from 'react';
import type { ConVersion } from '../../api/cliente';
import type { Cita, CitaConPago, Hueco, Presupuesto, Pregunta, ResumenCliente, ResultadoHuecos, Servicio, ServicioPedido } from '../../api/ops/citas';
import { ErrorApi } from '../../api/problema';
import { Ayuda } from '../../componentes/Ayuda';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Campo } from '../../componentes/Campo';
import { Casilla } from '../../componentes/Casilla';
import { Desplegable } from '../../componentes/Desplegable';
import { EstadoCargando, EstadoError } from '../../componentes/EstadoPagina';
import { Fecha } from '../../componentes/Fecha';
import { Icono } from '../../componentes/Icono';
import { Interruptor } from '../../componentes/Interruptor';
import { PanelLateral } from '../../componentes/PanelLateral';
import { Ventana } from '../../componentes/Ventana';
import { dinero } from '../../logica/dinero';
import { estadoDeError } from '../../logica/estados';
import { aMinutos } from '../../logica/horario';
import type { ContextoAgenda } from './contexto';
import { diaCorto, duracion, franjaDe, hm, horaEn, mayuscula, primerNombre, telefonoLegible, textoDeError, textoServicios, todas } from './logica';
import { AYUDA } from './textos';

/**
 * «Nueva cita» (9.2) y «Cambiar la cita» (9.3) en RV Panel. Los huecos los da la API (`searchAppointmentSlots`, con la
 * regla más estricta de los servicios, G485, G502); duración, precio y señal, `quoteAppointment` (G41, G317). El panel no
 * calcula nada de eso. Crear con señal deja la cita pendiente de pago 30 minutos (G39, G380); forzar el hueco o no cobrar,
 * con motivo y `citas.citas.forzar` (G38).
 */
export interface Desde {
    fecha: string;
    hora?: number;
    profesional?: string;
}

interface Nota {
    texto: string;
    icono: 'send' | 'link' | 'check';
    enlace?: string;
}

type Respuestas = Record<string, Record<string, { opciones?: string[]; numero?: string; texto?: string }>>;

const CUALQUIERA = 'any';

function pedidos(seleccion: readonly string[], servicios: readonly Servicio[], respuestas: Respuestas): ServicioPedido[] {
    return seleccion.map((id) => {
        const s = servicios.find((x) => x.id === id);
        const r = respuestas[id] ?? {};
        const answers = (s?.current_version.questions ?? [])
            .map((q) => {
                const v = r[q.id];
                if (v === undefined) {
                    return null;
                }
                if (q.kind === 'number') {
                    return v.numero === undefined || v.numero === '' ? null : { question_id: q.id, number_value: Number(v.numero) };
                }
                if (q.kind === 'text') {
                    return v.texto === undefined || v.texto.trim() === '' ? null : { question_id: q.id, text_value: v.texto.trim() };
                }
                return v.opciones === undefined || v.opciones.length === 0 ? null : { question_id: q.id, option_ids: v.opciones };
            })
            .filter((a) => a !== null);
        return answers.length === 0 ? { service_id: id } : { service_id: id, answers };
    });
}

/** Respuestas por defecto: la primera opción en las de una sola, el mínimo en las de número (como el diseño). */
function porDefecto(s: Servicio): Respuestas[string] {
    const r: Respuestas[string] = {};
    for (const q of s.current_version.questions) {
        if (q.kind === 'single_choice' && q.options?.[0] !== undefined) {
            r[q.id] = { opciones: [q.options[0].id] };
        } else if (q.kind === 'number' && q.number_min !== undefined) {
            r[q.id] = { numero: String(q.number_min) };
        }
    }
    return r;
}

const minutosDe = (h: Hueco) => aMinutos(h.local_time);

export function FormularioCita({
    ctx,
    desde,
    cita,
    onCerrar,
    onCreada,
}: {
    ctx: ContextoAgenda;
    /** Nueva cita: desde un hueco o desde «Nueva cita». */
    desde: Desde | null;
    /** Cambiar: la cita con su versión. */
    cita: ConVersion<Cita> | null;
    onCerrar: () => void;
    onCreada: (c: Cita, nota: Nota) => void;
}) {
    const cambiar = cita !== null;
    const original = cita?.datos ?? null;
    const avisar = useAvisoBreve();
    const hoy = ctx.ahora.fecha;
    const [servicios, setServicios] = useState<Servicio[] | null>(null);
    const [errorCarga, setErrorCarga] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [seleccion, setSeleccion] = useState<string[]>(() => original?.segments.map((s) => s.service_id) ?? []);
    const [respuestas, setRespuestas] = useState<Respuestas>({});
    const propio = ctx.datos.propio?.id;
    const [profesional, setProfesional] = useState<string>(() => (cambiar ? (original?.segments[0]?.professional_id ?? CUALQUIERA) : (desde?.profesional ?? (ctx.esProfesional && propio !== undefined ? propio : CUALQUIERA))));
    const [fecha, setFecha] = useState(() => (original !== null ? original.local_date : (desde?.fecha ?? hoy)));
    const [hora, setHora] = useState<number | null>(() => (original !== null ? aMinutos(original.local_time) : (desde?.hora ?? null)));
    const [horaPro, setHoraPro] = useState<string | null>(() => (original !== null ? (original.segments[0]?.professional_id ?? null) : (desde?.profesional ?? null)));
    const [presupuestoLeido, setPresupuesto] = useState<Presupuesto | null>(null);
    // La búsqueda de huecos va por su clave: mientras no llega la de lo elegido, «cargando»; si falla, error con «Reintentar» (9.2).
    const [busquedaHuecos, setBusquedaHuecos] = useState<{ clave: string; resultado: ResultadoHuecos | null } | null>(null);
    const [cliente, setCliente] = useState<ResumenCliente | null>(null);
    const [modo, setModo] = useState<'buscar' | 'alta'>('buscar');
    const [q, setQ] = useState('');
    const [resultadosLeidos, setResultados] = useState<ResumenCliente[] | null>(null);
    const [nNombre, setNNombre] = useState('');
    const [nTel, setNTel] = useState('');
    const [enviar, setEnviar] = useState(true);
    const [noCobrar, setNoCobrar] = useState(false);
    const [motivoNoCobrar, setMotivoNoCobrar] = useState('');
    const [forzar, setForzar] = useState(false);
    const [motivoForzar, setMotivoForzar] = useState('');
    const [motivoCambio, setMotivoCambio] = useState('');
    const [intentado, setIntentado] = useState(false);
    const [conflicto, setConflicto] = useState(false);
    const [noCabe, setNoCabe] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [confirmar, setConfirmar] = useState(false);
    const [buscarOtraVez, setBuscarOtraVez] = useState(0);

    useEffect(() => {
        let vivo = true;
        todas((cursor) => ctx.ops.listServices(ctx.negocio, { status: 'active', cursor, limit: 100 }))
            .then((l) => {
                if (vivo) {
                    setServicios(l);
                    setErrorCarga(null);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setErrorCarga(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ctx.ops, ctx.negocio, intento]);

    // Al cambiar una cita, sus respuestas de siempre (lo que se envía si no cambian los servicios).
    const peticion = useMemo<ServicioPedido[]>(() => {
        if (original !== null) {
            return original.segments.map((s) => {
                const answers = s.answers.map((a) => (a.option_id != null ? { question_id: a.question_id, option_ids: [a.option_id] } : a.number_value != null ? { question_id: a.question_id, number_value: a.number_value } : { question_id: a.question_id, text_value: a.text_value ?? '' }));
                return answers.length === 0 ? { service_id: s.service_id } : { service_id: s.service_id, answers };
            });
        }
        return pedidos(seleccion, servicios ?? [], respuestas);
    }, [original, seleccion, servicios, respuestas]);
    const clavePeticion = JSON.stringify(peticion);
    const proPedido = profesional === CUALQUIERA ? null : profesional;
    const claveHuecos = JSON.stringify([clavePeticion, proPedido, fecha, original?.id ?? null, buscarOtraVez]);

    useEffect(() => {
        if (peticion.length === 0) {
            return;
        }
        let vivo = true;
        ctx.ops
            .quoteAppointment(ctx.negocio, { services: peticion, professional_id: proPedido })
            .then((p) => {
                if (vivo) {
                    setPresupuesto(p);
                }
            })
            .catch(() => {
                if (vivo) {
                    setPresupuesto(null);
                }
            });
        return () => {
            vivo = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- la petición va por su clave
    }, [ctx.ops, ctx.negocio, clavePeticion, proPedido]);

    useEffect(() => {
        if (peticion.length === 0) {
            return;
        }
        let vivo = true;
        ctx.ops
            .searchAppointmentSlots(ctx.negocio, { services: peticion, professional_id: proPedido, mode: 'list', from: fecha, to: fecha, ...(original === null ? {} : { exclude_appointment_id: original.id }) })
            .then((h) => {
                if (vivo) {
                    setBusquedaHuecos({ clave: claveHuecos, resultado: h });
                }
            })
            .catch(() => {
                if (vivo) {
                    setBusquedaHuecos({ clave: claveHuecos, resultado: null });
                }
            });
        return () => {
            vivo = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- la petición va por su clave
    }, [ctx.ops, ctx.negocio, claveHuecos]);

    useEffect(() => {
        const texto = q.trim();
        if (texto.length < 2) {
            return;
        }
        let vivo = true;
        const t = setTimeout(() => {
            ctx.ops
                .searchAppointmentCustomers(ctx.negocio, texto)
                .then((l) => {
                    if (vivo) {
                        setResultados(l.data);
                    }
                })
                .catch(() => {
                    if (vivo) {
                        setResultados([]);
                    }
                });
        }, 250);
        return () => {
            vivo = false;
            clearTimeout(t);
        };
    }, [ctx.ops, ctx.negocio, q]);

    // Sin servicios ni búsqueda, lo leído antes no vale (se deriva aquí, no se borra en los efectos).
    const presupuesto = peticion.length === 0 ? null : presupuestoLeido;
    const leidaHuecos = busquedaHuecos?.clave === claveHuecos ? busquedaHuecos : null;
    const huecos = peticion.length === 0 ? null : (leidaHuecos?.resultado ?? null);
    const falloHuecos = peticion.length > 0 && leidaHuecos !== null && leidaHuecos.resultado === null;
    const resultados = q.trim().length < 2 ? null : resultadosLeidos;
    const corto = (id: string | null | undefined) => {
        const p = ctx.profesional(id);
        return p === undefined ? '' : primerNombre(p.display_name);
    };
    const lista = huecos?.slots ?? [];
    const elegido = hora === null ? null : (lista.find((h) => minutosDe(h) === hora) ?? null);
    const proDelHueco = elegido?.professional_id ?? horaPro ?? (proPedido ?? null);
    const elegibles = presupuesto?.eligible_professionals;
    const sinNadie = peticion.length > 0 && elegibles !== undefined && elegibles.length === 0;
    const durTotal = presupuesto?.total_duration_minutes ?? original?.total_duration_minutes ?? 0;
    const pago = presupuesto?.payment;
    const conSenal = !cambiar && pago?.requires_payment === true && pago.deposit !== undefined && pago.deposit.amount > 0;
    const diaTxt = (f: string) => (f === hoy ? 'hoy' : diaCorto(f));

    // Errores al intentar crear o guardar (los del diseño).
    const errores = intentado
        ? {
              servicio: !cambiar && seleccion.length === 0 ? 'Elige al menos un servicio.' : '',
              hueco: hora === null ? 'Elige un hueco.' : noCabe || (huecos !== null && elegido === null) ? `Con estos servicios ya no cabe a las ${hm(hora)}. Elige otra hora.` : '',
              cliente: !cambiar && modo === 'buscar' && cliente === null ? 'Elige al cliente o dalo de alta.' : '',
              nombre: !cambiar && modo === 'alta' && nNombre.trim() === '' ? 'Escribe su nombre.' : '',
              telefono: !cambiar && modo === 'alta' && nTel.replace(/\D/g, '').length < 9 ? 'Escribe un teléfono de 9 cifras.' : '',
              noCobrar: noCobrar && motivoNoCobrar.trim() === '' ? 'Escribe el motivo.' : '',
              forzar: forzar && motivoForzar.trim() === '' ? 'Escribe el motivo.' : '',
          }
        : { servicio: '', hueco: '', cliente: '', nombre: '', telefono: '', noCobrar: '', forzar: '' };

    const crear = () => {
        setIntentado(true);
        const e = {
            servicio: seleccion.length === 0,
            hueco: hora === null || elegido === null,
            cliente: modo === 'buscar' ? cliente === null : nNombre.trim() === '' || nTel.replace(/\D/g, '').length < 9,
            motivos: (noCobrar && motivoNoCobrar.trim() === '') || (forzar && motivoForzar.trim() === ''),
        };
        if (e.servicio || e.hueco || e.cliente || e.motivos || elegido === null) {
            return;
        }
        setOcupado(true);
        const quien = modo === 'buscar' && cliente !== null
            ? Promise.resolve({ id: cliente.id, nombre: cliente.display_name })
            : ctx.ops.createAppointmentCustomer(ctx.negocio, { display_name: nNombre.trim(), phone: nTel.trim(), source: 'panel' }).then((r) => ({ id: r.customer.id, nombre: r.customer.display_name }));
        quien
            .then((cl) =>
                ctx.ops
                    .createAppointment(ctx.negocio, {
                        local_date: elegido.local_date,
                        local_time: elegido.local_time,
                        services: peticion,
                        professional_id: proPedido,
                        customer_id: cl.id,
                        send_payment_link: enviar && !noCobrar,
                        ...(noCobrar && ctx.puede.forzar ? { waive_payment: { reason: motivoNoCobrar.trim() } } : {}),
                        ...(forzar && ctx.puede.forzar ? { override_window: { reason: motivoForzar.trim() } } : {}),
                    })
                    .then((c: CitaConPago) => {
                        onCreada(c, notaDeCreada(c, cl.nombre));
                    }),
            )
            .catch((err: unknown) => {
                if (err instanceof ErrorApi && err.estado === 409) {
                    setConflicto(true);
                    setHora(null);
                    setHoraPro(null);
                    setBuscarOtraVez((n) => n + 1);
                    return;
                }
                avisar({ tipo: 'error', texto: textoDeError(err) });
            })
            .finally(() => {
                setOcupado(false);
            });
    };

    const notaDeCreada = (c: CitaConPago, nombre: string): Nota => {
        const p = c.payment_action;
        const primero = primerNombre(nombre);
        if (p != null && p.delivered_to_customer === true) {
            return { texto: `Cita creada. Le hemos enviado a ${primero} el enlace para pagar la señal de ${dinero(p.amount)}: tiene 30 minutos.`, icono: 'send' };
        }
        if (p != null) {
            return { texto: `Cita creada. Pásale tú el enlace para pagar la señal de ${dinero(p.amount)}: caduca a las ${horaEn(p.expires_at, ctx.zona)}.`, icono: 'link', enlace: p.url };
        }
        return { texto: `Cita creada y confirmada. Le hemos enviado la confirmación a ${primero} por WhatsApp.`, icono: 'check' };
    };

    // ---- Cambiar ----
    const mismo = original !== null && hora === aMinutos(original.local_time) && fecha === original.local_date && (proDelHueco ?? '') === (original.segments[0]?.professional_id ?? '');
    const guardarCambio = () => {
        setIntentado(true);
        if (original === null || hora === null || elegido === null) {
            return;
        }
        if (mismo) {
            onCerrar();
            return;
        }
        setOcupado(true);
        ctx.ops
            .previewAppointmentModification(ctx.negocio, original.id, { local_date: elegido.local_date, local_time: elegido.local_time, professional_id: proPedido })
            .then((v) => {
                if (!v.available) {
                    setNoCabe(true);
                    setBuscarOtraVez((n) => n + 1);
                    return;
                }
                setConfirmar(true);
            })
            .catch((err: unknown) => {
                avisar({ tipo: 'error', texto: textoDeError(err) });
            })
            .finally(() => {
                setOcupado(false);
            });
    };
    const avisoPro = (antes: string | undefined, ahora: string | null) => {
        const yo = ctx.esProfesional ? propio : undefined;
        const ps = [antes, ahora].filter((x, i, l): x is string => x != null && x !== yo && l.indexOf(x) === i);
        return ps.length === 0 ? '' : ` ${ps.map((x) => corto(x)).join(' y ')} lo ${ps.length > 1 ? 'verán' : 'verá'} en su campana.`;
    };
    const cambiarYAvisar = () => {
        if (original === null || cita === null || elegido === null) {
            return;
        }
        setOcupado(true);
        ctx.ops
            .modifyAppointment(ctx.negocio, original.id, { local_date: elegido.local_date, local_time: elegido.local_time, professional_id: proPedido, ...(motivoCambio.trim() === '' ? {} : { reason: motivoCambio.trim() }) }, cita.version ?? `"${original.id}.${String(original.version)}"`)
            .then((r) => {
                const nueva = r.datos;
                const nuevoPro = nueva.segments[0]?.professional_id ?? null;
                const f = franjaDe(nueva);
                setConfirmar(false);
                onCreada(nueva, { texto: `Cita cambiada a ${diaCorto(f.fecha)}, ${hm(f.inicio)} con ${corto(nuevoPro)}. Le hemos avisado por WhatsApp con su pase nuevo; el de antes ya no vale.${avisoPro(original.segments[0]?.professional_id, nuevoPro)}`, icono: 'send' });
            })
            .catch((err: unknown) => {
                setConfirmar(false);
                if (err instanceof ErrorApi && err.estado === 409) {
                    setNoCabe(true);
                    setBuscarOtraVez((n) => n + 1);
                    return;
                }
                avisar({ tipo: 'error', texto: textoDeError(err) });
            })
            .finally(() => {
                setOcupado(false);
            });
    };

    // ---- Lo que se pinta ----
    const nombresSel = original !== null ? textoServicios(original) : seleccion.map((id) => servicios?.find((s) => s.id === id)?.current_version.name ?? '').join(' + ');
    const resumen = (() => {
        if (!cambiar && seleccion.length === 0) {
            return hora === null ? null : `Hueco elegido: ${diaTxt(fecha)}, ${hm(hora)}${horaPro !== null && corto(horaPro) !== '' ? ` con ${corto(horaPro)}` : ''}. Elige los servicios.`;
        }
        const conHueco = hora !== null ? ` · ${mayuscula(diaCorto(fecha))}, ${hm(hora)}${corto(proDelHueco) !== '' ? ` con ${corto(proDelHueco)}` : ''}` : '';
        return `${nombresSel} · ${duracion(durTotal)}${conHueco}`;
    })();
    const precio = cambiar ? dinero(original?.total_price) : seleccion.length === 0 ? '' : dinero(presupuesto?.total_price);
    const opcionesPro = [
        ...(ctx.esProfesional ? [] : [{ valor: CUALQUIERA, texto: 'Cualquiera (el menos ocupado)' }]),
        ...ctx.datos.profesionales.filter((p) => elegibles === undefined || peticion.length === 0 || elegibles.some((e) => e.id === p.id) || p.id === profesional).map((p) => ({ valor: p.id, texto: p.display_name })),
    ];
    const ayudaPro = cambiar && ctx.esProfesional ? 'Solo puedes moverla dentro de tu agenda.' : !cambiar && peticion.length > 0 && elegibles !== undefined && elegibles.length > 0 ? (elegibles.length === 1 ? `Solo lo hace ${primerNombre(elegibles[0]?.display_name ?? '')}.` : `Lo hacen ${elegibles.map((e) => primerNombre(e.display_name)).join(', ').replace(/, ([^,]*)$/, ' y $1')}.`) : '';
    const errorPro = sinNadie ? 'Ningún profesional hace todos estos servicios juntos.' : proPedido !== null && elegibles !== undefined && peticion.length > 0 && !elegibles.some((e) => e.id === proPedido) ? `${corto(proPedido)} no hace todos estos servicios.` : '';
    const vacioHuecos = sinNadie ? 'Ningún profesional hace todos estos servicios juntos. Quita alguno.' : fecha < hoy ? 'Ese día ya ha pasado.' : 'No queda ningún hueco donde quepa. Prueba otro día u otro profesional.';

    const cuerpo = (() => {
        if (servicios === null) {
            return errorCarga !== null ? <EstadoError seccion="los servicios" codigoSoporte={estadoDeError(errorCarga).codigoSoporte} onReintentar={() => { setIntento((i) => i + 1); }} movil={ctx.movil} /> : <EstadoCargando seccion="los servicios" />;
        }
        return (
            <div className="rv-ag-form">
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Ayuda tarjetas={cambiar ? AYUDA.cambiar : AYUDA.nueva} />
                </div>
                {resumen !== null && (
                    <div className="rv-ag-resumen">
                        <Icono nombre="calendar-clock" />
                        <span>{resumen}</span>
                        {precio !== '' && <span>{precio}</span>}
                    </div>
                )}
                {!cambiar && (
                    <section>
                        <div className="rv-ag-form__cab">
                            <span>Servicios</span>
                            <small>{seleccion.length > 0 ? `${String(seleccion.length)} elegido${seleccion.length > 1 ? 's' : ''} · en este orden` : 'Uno o varios'}</small>
                        </div>
                        {servicios.map((s) => {
                            const i = seleccion.indexOf(s.id);
                            const noLoHace = proPedido !== null && !s.professional_ids.includes(proPedido);
                            return (
                                <div key={s.id}>
                                    <Interruptor
                                        etiqueta={s.current_version.name}
                                        ayuda={`${duracion(s.current_version.base_duration_minutes)} · ${dinero(s.current_version.base_price)}${noLoHace ? ` · no lo hace ${corto(proPedido)}` : ''}`}
                                        nota={i >= 0 && seleccion.length > 1 ? `${String(i + 1)}.º` : undefined}
                                        valor={i >= 0}
                                        onChange={(v) => {
                                            setConflicto(false);
                                            setSeleccion((l) => (v ? [...l, s.id] : l.filter((x) => x !== s.id)));
                                            if (v && respuestas[s.id] === undefined) {
                                                setRespuestas((r) => ({ ...r, [s.id]: porDefecto(s) }));
                                            }
                                        }}
                                    />
                                    {i >= 0 &&
                                        s.current_version.questions.map((pq) => (
                                            <PreguntaServicio
                                                key={pq.id}
                                                pregunta={pq}
                                                servicio={s.current_version.name}
                                                valor={respuestas[s.id]?.[pq.id] ?? {}}
                                                onChange={(v) => {
                                                    setRespuestas((r) => ({ ...r, [s.id]: { ...(r[s.id] ?? {}), [pq.id]: v } }));
                                                }}
                                            />
                                        ))}
                                </div>
                            );
                        })}
                        {errores.servicio !== '' && <p className="rv-ag-error">{errores.servicio}</p>}
                    </section>
                )}
                <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div className="rv-ag-form__cab">
                        <span>Cuándo y con quién</span>
                    </div>
                    <Desplegable
                        etiqueta="Profesional"
                        valor={profesional}
                        opciones={opcionesPro}
                        ayuda={ayudaPro}
                        error={errorPro}
                        bloqueado={cambiar && ctx.esProfesional}
                        onChange={(v) => {
                            setProfesional(v);
                            setHora(null);
                            setHoraPro(null);
                            setConflicto(false);
                            setNoCabe(false);
                        }}
                    />
                    <Fecha
                        etiqueta="Día"
                        valor={fecha}
                        min={hoy}
                        onChange={(v) => {
                            if (v !== '') {
                                setFecha(v);
                                setHora(null);
                                setHoraPro(null);
                                setConflicto(false);
                                setNoCabe(false);
                            }
                        }}
                    />
                    {peticion.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <div className="rv-ag-form__cab">
                                <span>{fecha === hoy ? `Huecos libres hoy · ${duracion(durTotal)}` : `Huecos libres el ${diaCorto(fecha)} · ${duracion(durTotal)}`}</span>
                            </div>
                            {falloHuecos ? (
                                <div className="rv-ag-huecos-error" role="alert">
                                    <strong><Icono nombre="triangle-alert" />No he podido buscar los huecos libres</strong>
                                    <span>Puede ser la conexión. Lo que has elegido sigue aquí: prueba de nuevo.</span>
                                    <button type="button" className="rv-b rv-b-sec" onClick={() => { setBuscarOtraVez((n) => n + 1); }}><Icono nombre="refresh-cw" /><span>Reintentar</span></button>
                                </div>
                            ) : huecos === null ? (
                                <div className="rv-ag-huecos-cargando" aria-busy="true" aria-label="Buscando huecos libres">
                                    <div>{[0, 1, 2, 3, 4, 5].map((n) => <span key={n} style={{ animationDelay: `${String(n * 70)}ms` }} />)}</div>
                                    <small>Buscando huecos donde quepan los servicios…</small>
                                </div>
                            ) : lista.length === 0 ? (
                                <p className="rv-ag-pie">{vacioHuecos}</p>
                            ) : (
                                <div className="rv-ag-huecos" role="group" aria-label="Huecos libres">
                                    {lista.slice(0, 24).map((h) => (
                                        <button
                                            key={`${h.local_date}${h.local_time}`}
                                            type="button"
                                            className="rv-ag-hueco"
                                            aria-pressed={hora === minutosDe(h)}
                                            onClick={() => {
                                                setHora(minutosDe(h));
                                                setHoraPro(h.professional_id ?? null);
                                                setConflicto(false);
                                                setNoCabe(false);
                                            }}
                                        >
                                            {hm(minutosDe(h))}
                                            {profesional === CUALQUIERA && h.professional_id !== undefined && <small>{corto(h.professional_id)}</small>}
                                        </button>
                                    ))}
                                </div>
                            )}
                            {errores.hueco !== '' && !conflicto && <p className="rv-ag-error">{errores.hueco}</p>}
                        </div>
                    )}
                    {conflicto && (
                        <div className="rv-ag-caja" style={{ background: 'var(--rv-red-soft)' }} role="alert">
                            <div className="rv-ag-caja__titulo">
                                <Icono nombre="triangle-alert" />
                                <span>Ese hueco se acaba de ocupar</span>
                            </div>
                            <p>Otra persona lo ha pedido a la vez. Te marcamos arriba los huecos que siguen libres: elige uno y vuelve a crearla.</p>
                        </div>
                    )}
                    {!cambiar && ctx.puede.forzar && fecha === hoy && (
                        <div>
                            <Interruptor etiqueta="Saltarse la antelación o el horizonte" ayuda="Ofrece también los huecos que la regla no deja pedir." valor={forzar} onChange={setForzar} />
                            {forzar && <Campo etiqueta="Motivo" valor={motivoForzar} onChange={setMotivoForzar} error={errores.forzar} ayuda="Queda en Auditoría a tu nombre." max={200} mayusculas="sentences" />}
                        </div>
                    )}
                    {cambiar && original !== null && hora !== null && !mismo && (
                        <div className="rv-ag-caja" style={{ background: 'var(--rv-bone)' }}>
                            <div className="rv-ag-caja__titulo">
                                <Icono nombre="calendar-clock" />
                                <span>{`De ${diaCorto(original.local_date)}, ${hm(aMinutos(original.local_time))} con ${corto(original.segments[0]?.professional_id)} a ${diaCorto(fecha)}, ${hm(hora)} con ${corto(proDelHueco)}`}</span>
                            </div>
                            <p>Mismos servicios y mismo precio: lo que ha pagado se mantiene. Le avisamos del cambio por WhatsApp.</p>
                        </div>
                    )}
                    {cambiar && <Campo etiqueta="Motivo del cambio" valor={motivoCambio} onChange={setMotivoCambio} ayuda="Opcional. Queda en el historial de la cita." max={500} mayusculas="sentences" />}
                </section>
                {!cambiar && (
                    <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <div className="rv-ag-form__cab">
                            <span>Cliente</span>
                        </div>
                        {modo === 'buscar' && cliente !== null && (
                            <div className="rv-ag-resultado" style={{ cursor: 'default' }}>
                                <span>
                                    <strong>{cliente.display_name}</strong>
                                    <small>{`${telefonoLegible(cliente.phone)} · cliente de tu negocio`}</small>
                                </span>
                                <button type="button" className="rv-bi" aria-label="Quitar" onClick={() => { setCliente(null); setQ(''); }}>
                                    <Icono nombre="x" />
                                </button>
                            </div>
                        )}
                        {modo === 'buscar' && cliente === null && (
                            <>
                                <Campo tipo="search" etiqueta="Buscar por nombre, teléfono o email" valor={q} onChange={setQ} error={errores.cliente} max={100} nombre="buscar-cliente-cita" />
                                {(resultados ?? []).map((r) => (
                                    <button key={r.id} type="button" className="rv-ag-resultado" onClick={() => { setCliente(r); setQ(''); }}>
                                        <span>
                                            <strong>{r.display_name}</strong>
                                            <small>{telefonoLegible(r.phone)}</small>
                                        </span>
                                        <Icono nombre="chevron-right" />
                                    </button>
                                ))}
                                {resultados !== null && resultados.length === 0 && <p className="rv-ag-pie">{q.replace(/\D/g, '').length >= 9 ? 'No hay ningún cliente de tu negocio con ese teléfono.' : 'No hay ningún cliente de tu negocio con ese nombre.'}</p>}
                                {ctx.puede.crearClientes && (
                                    <div>
                                        <button
                                            type="button"
                                            className="rv-ag-b"
                                            onClick={() => {
                                                const tel = q.replace(/\D/g, '').length >= 9;
                                                setNNombre(tel ? '' : q);
                                                setNTel(tel ? q : '');
                                                setModo('alta');
                                            }}
                                        >
                                            <Icono nombre="plus" />
                                            <span>Dar de alta un cliente</span>
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                        {modo === 'alta' && (
                            <>
                                <Campo etiqueta="Nombre y apellidos" valor={nNombre} onChange={setNNombre} error={errores.nombre} autocompletar="off" mayusculas="words" max={120} nombre="cita-cliente-nombre" />
                                <Campo etiqueta="Teléfono" tipo="tel" valor={nTel} onChange={setNTel} error={errores.telefono} ayuda="No se consulta su Cuenta Reservaria." autocompletar="off" max={20} nombre="cita-cliente-telefono" />
                                <div>
                                    <button type="button" className="rv-ag-b" onClick={() => { setModo('buscar'); }}>
                                        <Icono nombre="search" />
                                        <span>Buscar uno que ya existe</span>
                                    </button>
                                </div>
                            </>
                        )}
                    </section>
                )}
                {conSenal && pago.deposit !== undefined && (
                    <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <div className="rv-ag-form__cab">
                            <span>Señal</span>
                        </div>
                        <div className="rv-ag-servicio">
                            <span>
                                <strong>{seleccion.length > 1 ? 'Señal (suma de los servicios)' : 'Señal'}</strong>
                                <small>{noCobrar ? 'No se cobra: la cita se confirma al momento.' : 'Queda pendiente de pago 30 minutos. Si no paga, el hueco se libera.'}</small>
                            </span>
                            <strong style={{ textDecoration: noCobrar ? 'line-through' : 'none', opacity: noCobrar ? 0.5 : 1 }}>{dinero(pago.deposit)}</strong>
                        </div>
                        <Interruptor etiqueta="Enviarle el enlace de pago" ayuda={enviar ? 'Por WhatsApp, con el email de respaldo.' : 'Te damos el enlace para que se lo pases tú.'} valor={enviar && !noCobrar} desactivado={noCobrar} onChange={setEnviar} />
                        {ctx.puede.forzar && (
                            <div>
                                <Interruptor etiqueta="No cobrar la señal" ayuda="La cita se confirma sin pagar. Queda en Auditoría." valor={noCobrar} onChange={setNoCobrar} />
                                {noCobrar && <Campo etiqueta="Motivo" valor={motivoNoCobrar} onChange={setMotivoNoCobrar} error={errores.noCobrar} ayuda="Por ejemplo, clienta de toda la vida." max={200} mayusculas="sentences" />}
                            </div>
                        )}
                        {pago.conditions_text !== undefined && pago.conditions_text !== '' && <p className="rv-ag-pie">{pago.conditions_text}</p>}
                    </section>
                )}
            </div>
        );
    })();

    return (
        <>
            <PanelLateral
                abierto
                onCerrar={onCerrar}
                movil={ctx.movil}
                tamano="formulario"
                icono={cambiar ? undefined : 'calendar-plus'}
                eyebrow={cambiar ? 'Cambiar la cita' : 'Cita'}
                titulo={cambiar && original !== null ? ctx.nombreDe(original) : 'Nueva cita'}
                subtitulo={cambiar && original !== null ? `${textoServicios(original)} · ${duracion(original.total_duration_minutes)}` : 'La clienta recibe la confirmación por su canal.'}
                accion={cambiar ? 'Guardar el cambio' : 'Crear cita'}
                accionIcono={cambiar ? 'check' : 'calendar-plus'}
                ocupado={ocupado && !confirmar}
                ocupadoTexto={cambiar ? 'Guardando…' : 'Creando…'}
                cerrar={cambiar ? 'Volver a la cita' : 'Cerrar'}
                onAccion={cambiar ? guardarCambio : crear}
            >
                {cuerpo}
            </PanelLateral>
            {cambiar && original !== null && (
                <Ventana
                    abierta={confirmar}
                    titulo="Cambiar la cita"
                    texto={hora === null ? '' : `Pasa de ${diaCorto(original.local_date)}, ${hm(aMinutos(original.local_time))} con ${corto(original.segments[0]?.professional_id)} a ${diaCorto(fecha)}, ${hm(hora)} con ${corto(proDelHueco)}. Le avisamos a ${primerNombre(ctx.nombreDe(original))} por WhatsApp; lo que ha pagado se mantiene.`}
                    cancelar="Volver"
                    accion="Cambiar y avisar"
                    accionIcono="send"
                    ocupado={ocupado}
                    ocupadoTexto="Cambiando…"
                    movil={ctx.movil}
                    onCerrar={() => {
                        if (!ocupado) {
                            setConfirmar(false);
                        }
                    }}
                    onAccion={cambiarYAvisar}
                />
            )}
        </>
    );
}

function PreguntaServicio({ pregunta, servicio, valor, onChange }: { pregunta: Pregunta; servicio: string; valor: { opciones?: string[]; numero?: string; texto?: string }; onChange: (v: { opciones?: string[]; numero?: string; texto?: string }) => void }) {
    const etiqueta = `${pregunta.label} · ${servicio}`;
    const textoOpcion = (o: NonNullable<Pregunta['options']>[number]) =>
        o.minutes_delta === 0 && o.price_delta.amount === 0 ? o.label : `${o.label} · ${o.minutes_delta >= 0 ? '+' : '−'}${String(Math.abs(o.minutes_delta))} min, ${o.price_delta.amount >= 0 ? '+' : ''}${dinero(o.price_delta)}`;
    const ayuda = pregunta.help_text ?? 'Cambia la duración y el precio.';
    if (pregunta.kind === 'single_choice') {
        return (
            <div style={{ paddingTop: 12 }}>
                <Desplegable etiqueta={etiqueta} valor={valor.opciones?.[0] ?? ''} opciones={(pregunta.options ?? []).map((o) => ({ valor: o.id, texto: textoOpcion(o) }))} ayuda={ayuda} onChange={(v) => { onChange({ opciones: [v] }); }} />
            </div>
        );
    }
    if (pregunta.kind === 'multiple_choice') {
        const sel = valor.opciones ?? [];
        return (
            <div style={{ paddingTop: 12 }}>
                <div className="rv-ag-form__cab">
                    <span>{etiqueta}</span>
                </div>
                {(pregunta.options ?? []).map((o) => (
                    <Casilla key={o.id} marcada={sel.includes(o.id)} onCambio={(m) => { onChange({ opciones: m ? [...sel, o.id] : sel.filter((x) => x !== o.id) }); }}>
                        {textoOpcion(o)}
                    </Casilla>
                ))}
            </div>
        );
    }
    return (
        <div style={{ paddingTop: 12 }}>
            <Campo etiqueta={etiqueta} tipo={pregunta.kind === 'number' ? 'number' : 'text'} valor={pregunta.kind === 'number' ? (valor.numero ?? '') : (valor.texto ?? '')} ayuda={pregunta.help_text} max={pregunta.text_max_length} onChange={(v) => { onChange(pregunta.kind === 'number' ? { numero: v } : { texto: v }); }} />
        </div>
    );
}
